// markup/MarkupToolbar.jsx — DropPin plan-markup tool palette.
// ---------------------------------------------------------------------------
// Presentational floating toolbar driven entirely by the useMarkup controller
// (`m`). Styling uses the shared suite tokens (chrome + brand via theme.js /
// tokens.css) so it matches DropPin's existing dark floating controls (the zoom
// cluster) rather than introducing a bespoke look. Annotation colour swatches
// are DATA values the user picks (like the trades palette), so literal colours
// are correct there — the chrome itself uses tokens only.
// ---------------------------------------------------------------------------

import React from 'react';
import { theme } from '../theme';
import {
  MousePointer2, Hand, Minus, MoveUpRight, Square, Hexagon, Spline, Pencil,
  Type, Ruler, Undo2, Redo2, Scissors, Copy, ClipboardPaste, Trash2, X, Crosshair,
  Sigma, LayoutGrid, Check, Ban, Download, List, Tag,
} from 'lucide-react';
import { calibrationLooksWrong } from './scaleCheck.mjs';
import { retypePlan, retypeTitle, retypeCount, withMarkType, typeChipPlan, typeChipTitle } from './model.mjs';
import MarkTypePicker from './MarkTypePicker.jsx';
// 2026-10-19 — a refusal because the sign-in ended goes to the suite sign-in, not an alert.
import { sessionHasEnded } from '../utils/sessionEnd';

const PALETTE = ['#e8650a', '#dc2626', '#2563eb', '#16a34a', '#f59e0b', '#0f172a', '#ffffff'];
const WIDTHS = [2, 4, 6];

const barBtn = {
  width: 30, height: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  border: 'none', borderRadius: 6, background: 'transparent', color: 'var(--chrome-text)',
  cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: 12, fontWeight: 700,
};

function IconBtn({ icon: Icon, label, active, disabled, onClick }) {
  return (
    <button type="button" title={label} aria-label={label} onClick={onClick} disabled={disabled}
      style={{
        ...barBtn,
        background: active ? theme.brand : 'transparent',
        color: active ? '#fff' : 'var(--chrome-text)',
        opacity: disabled ? 0.35 : 1, cursor: disabled ? 'default' : 'pointer',
      }}>
      <Icon size={17} strokeWidth={2} />
    </button>
  );
}

function TextBtn({ children, label, active, onClick }) {
  return (
    <button type="button" title={label} aria-label={label} onClick={onClick}
      style={{ ...barBtn, width: 'auto', padding: '0 8px', background: active ? theme.brand : 'rgba(255,255,255,0.06)', color: active ? '#fff' : 'var(--chrome-text)' }}>
      {children}
    </button>
  );
}

function Divider() {
  return <span style={{ width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,0.16)', margin: '2px 3px' }} />;
}

export default function MarkupToolbar({ m, onExit, drawingId, markTypes, naturalW = 0, naturalH = 0 }) {
  const isTool = (t) => m.tool === t;
  const hasSel = m.selectedIds.length > 0;
  const calibrated = !!m.calibration;
  // 2026-09-15 — a scale that makes the sheet kilometres across (a millimetre
  // dimension typed as metres) says so on the button, not only in the numbers.
  const scaleSuspect = calibrated && calibrationLooksWrong(m.calibration, naturalW, naturalH);

  // 2026-10-21 — Brett: "we should be able to select multiple areas and change
  // its type at once." The same menu a double-tap opens, for the whole
  // selection, so it does not depend on landing a double-tap inside one area.
  const [typeMenu, setTypeMenu] = React.useState(null); // { x, y, el } under the button
  const selSet = new Set(m.selectedIds.map(String));
  const selectedMarks = m.objects.filter((o) => selSet.has(String(o.id)));
  const typePlan = retypePlan(selectedMarks, null, markTypes);
  const canRetype = typePlan.types.length > 0;
  React.useEffect(() => { if (!canRetype) setTypeMenu(null); }, [canRetype]);
  const toggleTypeMenu = (e) => {
    if (typeMenu) { setTypeMenu(null); return; }
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    setTypeMenu({ x: r.left, y: r.bottom, el });
  };
  const pickType = (t) => {
    setTypeMenu(null);
    if (typePlan.targets.length) m.updateObjects(typePlan.targets.map((o) => withMarkType(o, t)));
  };

  // v0.147.0 — TAKE IT BACK TO INSPEC. Downloads this plan as a PDF with the
  // marks written in as an editable take-off, so opening it in InSpecPDF shows
  // real measurements rather than ink. Brett: "edit it if i take it back to
  // inspec." A download rather than a push, because DropPin holds an imported
  // COPY — deciding remotely that its version beats the office's is a merge a
  // person should make, not something that happens silently.
  const [sending, setSending] = React.useState(false);
  const takeBackToInSpec = async () => {
    if (!drawingId || sending) return;
    setSending(true);
    try {
      const r = await fetch(`/api/plan-takeoff-export?drawingId=${encodeURIComponent(drawingId)}`,
        { credentials: 'same-origin' });
      if (!r.ok) {
        if (r.status === 401 && await sessionHasEnded('/api/plan-takeoff-export')) return;
        let msg = 'Could not prepare the file.';
        try { msg = ((await r.json()) || {}).error || msg; } catch (_) { /* keep the default */ }
        alert(msg);
        return;
      }
      const blob = await r.blob();
      const cd = r.headers.get('content-disposition') || '';
      const named = /filename="?([^";]+)"?/i.exec(cd);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (named && named[1]) || 'plan.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (_) {
      alert("Couldn't reach the server. Check your connection and try again.");
    } finally {
      setSending(false);
    }
  };

  const wrap = {
    position: 'absolute', top: 8, left: 8, zIndex: 20, maxWidth: 'calc(100% - 16px)',
    display: 'flex', flexDirection: 'column', gap: 6,
    background: 'var(--chrome-800)', border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 10, padding: 6, boxShadow: 'var(--shadow-3)',
  };
  const row = { display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' };

  return (
    <div style={wrap} onPointerDown={(e) => e.stopPropagation()} onWheel={(e) => e.stopPropagation()}>
      {/* Row 1 — tools */}
      <div style={row}>
        <IconBtn icon={MousePointer2} label="Select (V) — drag a mark to move, drag its handles to resize / reshape, drag empty canvas to marquee-select (Shift adds); hold Shift while drawing for 45°" active={isTool('select')} onClick={() => m.setTool('select')} />
        <IconBtn icon={Hand} label="Pan / navigate the plan" active={isTool('pan')} onClick={() => m.setTool('pan')} />
        <Divider />
        <IconBtn icon={Minus} label="Line (hold Shift for 0/45/90°)" active={isTool('line')} onClick={() => m.setTool('line')} />
        <IconBtn icon={MoveUpRight} label="Arrow (hold Shift for 0/45/90°)" active={isTool('arrow')} onClick={() => m.setTool('arrow')} />
        <IconBtn icon={Square} label="Rectangle" active={isTool('rect')} onClick={() => m.setTool('rect')} />
        <IconBtn icon={Hexagon} label="Polygon (click points, double-click or Enter to finish)" active={isTool('polygon')} onClick={() => m.setTool('polygon')} />
        <IconBtn icon={Spline} label="Polyline (click points, double-click or Enter to finish)" active={isTool('polyline')} onClick={() => m.setTool('polyline')} />
        <IconBtn icon={Pencil} label="Freehand" active={isTool('freehand')} onClick={() => m.setTool('freehand')} />
        <IconBtn icon={Type} label="Text note" active={isTool('text')} onClick={() => m.setTool('text')} />
        <Divider />
        <IconBtn icon={Ruler} label="Measure (length / area)" active={isTool('measure')} onClick={() => m.setTool('measure')} />
        {isTool('measure') && (
          <>
            <TextBtn label="Measure length" active={m.measureKind === 'length'} onClick={() => m.setMeasureKind('length')}>Len</TextBtn>
            <TextBtn label="Measure area" active={m.measureKind === 'area'} onClick={() => m.setMeasureKind('area')}>Area</TextBtn>
          </>
        )}
        <IconBtn icon={Sigma} label="Take-off totals — roll up every measurement (length + area)" active={!!m.showTotals} onClick={() => m.setShowTotals(!m.showTotals)} />
        <Divider />
        {drawingId ? (
          <IconBtn
            icon={Download}
            label={sending
              ? 'Preparing…'
              : 'Take back to InSpec — downloads this plan with your marks as an editable take-off'}
            disabled={sending}
            onClick={takeBackToInSpec}
          />
        ) : null}
        <IconBtn icon={X} label="Close markup" onClick={onExit} />
      </div>

      {/* Row 1a — THE TENANT'S OWN MARK TYPES (2026-08-14).
          Brett: "1 tool would be new tiles and be coloured red, full regrout and
          be coloured yellow, patch regrout and be coloured green."

          Picking one is a single action that sets three things: the tool (an
          area type draws areas), the colour, and — the part that matters — the
          TYPE the mark is recorded as. That is what lets the take-off say
          "new tiles 42 m², full regrout 18 m" instead of one undifferentiated
          pile of areas.

          Absent until a set is loaded, so a tenant who has never opened Settings
          sees exactly the toolbar they see today.

          2026-10-21 — Brett, an area selected: "what do i push to change the tag
          to full regrout?", then "yes make it work that way." With marks
          selected that a type fits, its button changes them (one undo step) and
          lights when they all have it; with none selected it arms the type for
          the next shape, as above (typeChipPlan). */}
      {Array.isArray(markTypes) && markTypes.length > 0 && (
        <div style={{ ...row, gap: 4 }}>
          {markTypes.filter((t) => t.active !== false).map((t) => {
            const chip = typeChipPlan(selectedMarks, t);
            const forSelection = chip.fits.length > 0;
            const on = forSelection ? chip.on : !!(m.markType && m.markType.key === t.key);
            const press = () => {
              if (!forSelection) { m.setMarkType(on ? null : t); return; }
              if (chip.changes.length) m.updateObjects(chip.changes.map((o) => withMarkType(o, t)));
            };
            return (
              <button key={t.key} type="button" onClick={press} aria-pressed={on}
                title={forSelection ? typeChipTitle(chip, t)
                  : `${t.label} — ${t.kind === 'area' ? 'area, m²' : t.kind === 'length' ? 'run, m' : 'a pin'}`}
                style={{
                  ...barBtn, width: 'auto', gap: 6, padding: '0 10px',
                  background: on ? t.color : 'rgba(255,255,255,0.06)',
                  color: on ? '#fff' : 'var(--chrome-text)',
                  border: on ? 'none' : '1px solid rgba(255,255,255,0.12)',
                }}>
                <span style={{
                  width: 10, height: 10, borderRadius: 2, background: t.color,
                  boxShadow: on ? '0 0 0 1px rgba(255,255,255,0.9)' : 'none', flex: '0 0 auto',
                }} />
                {t.label}
              </button>
            );
          })}
          <Divider />
          {/* 2026-09-15 — Brett: "make me a legend button so i can stamp the
              drawing with those colours." Arms a one-tap stamp; press it again
              to put it away. */}
          <button type="button" onClick={() => m.setTool(isTool('legend') ? 'select' : 'legend')}
            title="Legend — tap the plan to stamp a key of these colours with what each adds up to"
            style={{
              ...barBtn, width: 'auto', gap: 6, padding: '0 10px',
              background: isTool('legend') ? theme.brand : 'rgba(255,255,255,0.06)',
              color: isTool('legend') ? '#fff' : 'var(--chrome-text)',
              border: isTool('legend') ? 'none' : '1px solid rgba(255,255,255,0.12)',
            }}>
            <List size={15} strokeWidth={2.2} />
            {isTool('legend') ? 'Tap the plan to place it' : 'Legend'}
          </button>
        </div>
      )}

      {/* Row 1b — the command in progress. An iPad has no Esc and no Enter, so
          without these buttons there is no way to back out of a half-drawn area
          or to close one off. Shown only while something is actually running. */}
      {m.commandActive && (
        <div style={{ ...row, gap: 6, padding: '2px 2px 0' }}>
          <button type="button" onClick={m.finishCommand} title="Finish this shape (Enter)"
            style={{ ...barBtn, width: 'auto', gap: 6, padding: '0 10px', background: theme.brand, color: '#fff' }}>
            <Check size={15} strokeWidth={2.4} /> Finish
          </button>
          <button type="button" onClick={m.cancelCommand} title="Cancel this shape (Esc)"
            style={{ ...barBtn, width: 'auto', gap: 6, padding: '0 10px', background: 'rgba(255,255,255,0.10)' }}>
            <Ban size={15} strokeWidth={2.2} /> Cancel
          </button>
          <span style={{ fontSize: 11, color: 'var(--chrome-muted)', paddingLeft: 4 }}>
            Middle-drag or hold Space to pan · two fingers on a tablet
          </span>
        </div>
      )}

      {/* Row 2 — edit actions + style + measure config */}
      <div style={row}>
        <IconBtn icon={Undo2} label="Undo (Ctrl+Z)" disabled={!m.canUndo} onClick={m.undo} />
        <IconBtn icon={Redo2} label="Redo (Ctrl+Y)" disabled={!m.canRedo} onClick={m.redo} />
        <Divider />
        <IconBtn icon={Scissors} label="Cut (Ctrl+X)" disabled={!hasSel} onClick={m.cut} />
        <IconBtn icon={Copy} label="Copy (Ctrl+C)" disabled={!hasSel} onClick={m.copy} />
        <IconBtn icon={ClipboardPaste} label="Paste (Ctrl+V)" onClick={m.paste} />
        <IconBtn icon={Trash2} label="Delete (Del)" disabled={!hasSel} onClick={m.deleteSelected} />
        <IconBtn icon={Tag} label={canRetype ? `Change type — ${retypeCount(typePlan) || 'the selected mark'}` : 'Change type — select one or more marks first'}
          active={!!typeMenu} disabled={!canRetype} onClick={toggleTypeMenu} />
        {typeMenu && canRetype && (
          <MarkTypePicker x={typeMenu.x} y={typeMenu.y} anchor={typeMenu.el} types={typePlan.types}
            title={retypeTitle(typePlan)} currentKey={typePlan.currentKey} canClear={typePlan.anyTyped}
            onPick={pickType} onClose={() => setTypeMenu(null)} />
        )}
        <Divider />
        <IconBtn icon={LayoutGrid} label="Grid snap — snap points to a light grid (vertex snapping is always on)" active={!!m.snapGrid} onClick={() => m.setSnapGrid(!m.snapGrid)} />
        <Divider />
        {/* Colour swatches */}
        <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center', padding: '0 2px' }}>
          {PALETTE.map((c) => (
            <button key={c} type="button" title={`Colour ${c}`} aria-label={`Colour ${c}`}
              onClick={() => m.setDrawStyle({ ...m.drawStyle, stroke: c })}
              style={{
                width: 18, height: 18, borderRadius: '50%', background: c, cursor: 'pointer', padding: 0,
                border: m.drawStyle.stroke === c ? '2px solid #fff' : '1px solid rgba(255,255,255,0.35)',
                boxShadow: m.drawStyle.stroke === c ? '0 0 0 1px ' + theme.brand : 'none',
              }} />
          ))}
        </span>
        <Divider />
        {/* Stroke width */}
        <span style={{ display: 'inline-flex', gap: 2, alignItems: 'center' }}>
          {WIDTHS.map((wv) => (
            <button key={wv} type="button" title={`Stroke ${wv}px`} aria-label={`Stroke ${wv}px`}
              onClick={() => m.setDrawStyle({ ...m.drawStyle, strokeWidth: wv })}
              style={{ ...barBtn, width: 26, background: m.drawStyle.strokeWidth === wv ? theme.brand : 'rgba(255,255,255,0.06)' }}>
              <span style={{ width: 14, height: wv, borderRadius: wv, background: m.drawStyle.strokeWidth === wv ? '#fff' : 'var(--chrome-text)', display: 'inline-block' }} />
            </button>
          ))}
        </span>
        <Divider />
        {/* Unit toggle */}
        <span style={{ display: 'inline-flex', gap: 2 }}>
          {['mm', 'm', 'ft'].map((u) => (
            <TextBtn key={u} label={`Show measurements in ${u}`} active={m.unit === u} onClick={() => m.setUnit(u)}>{u}</TextBtn>
          ))}
        </span>
        <Divider />
        {/* Calibration */}
        <button type="button" onClick={() => m.setTool('calibrate')} title={scaleSuspect
            ? 'This scale makes the sheet far too big or too small — draw the line again and check the unit'
            : 'Calibrate scale — draw a line over a known dimension, then type its real length'}
          style={{
            ...barBtn, width: 'auto', gap: 5, padding: '0 8px',
            background: isTool('calibrate') ? theme.brand
              : scaleSuspect ? theme.warn
              : (calibrated ? 'rgba(22,163,74,0.22)' : 'rgba(255,255,255,0.06)'),
            color: (isTool('calibrate') || scaleSuspect) ? '#fff' : 'var(--chrome-text)',
          }}>
          <Crosshair size={15} strokeWidth={2} />
          {scaleSuspect ? 'Check scale' : calibrated ? 'Scale set' : 'Set scale'}
        </button>
        {calibrated && (
          <button type="button" onClick={m.clearCalibration} title="Clear calibration"
            style={{ ...barBtn, width: 22, color: 'var(--chrome-muted)' }}>×</button>
        )}
      </div>
    </div>
  );
}
